/**
 * Platform dashboard — top-level KPI snapshot.
 *
 * Wallet totals use the same rules as frontend Wallet 资产概况:
 * master accounts only, ORB(主+子) display algorithm, frozen vs active split.
 */
import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Divider, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TOKEN_META, tokenDisplayLabel } from '@uo/shared';
import { api } from '../../api/client';

/** Mirrors backend DASHBOARD_WALLET_FIELDS + frontend BALANCE_CELL_KEYS. */
type DashboardWalletField =
  | 'sp1'
  | 'sp2'
  | 'ep1'
  | 'ep2'
  | 'fc'
  | 'uo'
  | 'uo_total'
  | 'uo_sell'
  | 'fp'
  | 'tp'
  | 'rp'
  | 'ep'
  | 'sp3'
  | 'ap'
  | 'ap_queue'
  | 'mp';

interface PreservedOrbRow {
  player_number: string;
  id: number;
  account: string;
  freeze: 'frozen' | 'active';
  uo_main: string;
  uo_total: string;
  sub_count: number;
}

interface Kpi {
  total_users: number;
  active_users: number;
  frozen_users: number;
  virtual_users: number;
  genealogy_users?: number;
  out_of_network_users?: number;
  php_only_out_of_network?: number;
  active_users_24h: number;
  recharge_pending: number;
  withdraw_pending: number;
  ep_open_orders: number;
  total_uo_balance: string;
  wallet_frozen?: Record<DashboardWalletField, string>;
  wallet_active?: Record<DashboardWalletField, string>;
  wallet_virtual?: Record<DashboardWalletField, string>;
  preserved_orb_accounts?: PreservedOrbRow[];
  preserved_orb_sum_uo_main?: string;
  preserved_orb_sum_uo_total?: string;
}

const WALLET_DISPLAY_ORDER: DashboardWalletField[] = [
  'sp1',
  'sp2',
  'ep1',
  'ep2',
  'fc',
  'uo',
  'uo_total',
  'uo_sell',
  'fp',
  'tp',
  'rp',
  'ep',
  'sp3',
  'ap',
  'ap_queue',
  'mp',
];

const WALLET_LABELS: Record<DashboardWalletField, string> = {
  uo: 'ORB(主)',
  uo_total: 'ORB(主+子)',
  uo_sell: '挂卖ORB数',
  fp: 'FP(ORB)',
  tp: 'TP交易点数',
  rp: 'RP注册点数',
  ep: 'EP转换点数',
  ep1: 'EP1现金点数',
  ep2: 'EP2现金点数',
  sp1: 'SP1复投点数',
  sp2: 'SP2复投点数',
  sp3: 'SP3复投点数',
  ap: 'AP兑捐 ORB',
  ap_queue: 'AP排队点数',
  mp: 'MP点数',
  fc: 'FC(ORB燃料)',
};

function walletCardTitle(field: DashboardWalletField): string {
  if (field === 'uo' || field === 'uo_total' || field === 'uo_sell') {
    return WALLET_LABELS[field];
  }
  const tokenKey = field as keyof typeof TOKEN_META;
  if (TOKEN_META[tokenKey]) {
    return `${tokenDisplayLabel(tokenKey)} ${TOKEN_META[tokenKey].name}`;
  }
  return WALLET_LABELS[field];
}

function statNum(
  data: Kpi | undefined,
  key: Exclude<keyof Kpi, 'wallet_frozen' | 'wallet_active'>,
): number | string {
  if (!data) return '—';
  const v = data[key];
  return typeof v === 'object' ? '—' : (v ?? '—');
}

function trimDecimalZeros(s: string): string {
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/** 中文数量级：万 / 百万 / 千万 / 亿（替代 M、B） */
function fmtWallet(v: string | undefined): string {
  if (!v || v === '0') return '0';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e8) return `${sign}${trimDecimalZeros((abs / 1e8).toFixed(2))}亿`;
  if (abs >= 1e7) return `${sign}${trimDecimalZeros((abs / 1e7).toFixed(2))}千万`;
  if (abs >= 1e6) return `${sign}${trimDecimalZeros((abs / 1e6).toFixed(2))}百万`;
  if (abs >= 1e4) return `${sign}${trimDecimalZeros((abs / 1e4).toFixed(2))}万`;
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 6 });
}

function WalletSection({
  title,
  sums,
  loading,
  valueColor,
}: {
  title: string;
  sums: Record<DashboardWalletField, string> | undefined;
  loading: boolean;
  valueColor?: string;
}) {
  return (
    <>
      <Divider orientation="left" style={{ marginTop: 8 }}>
        {title}
      </Divider>
      <Row gutter={[12, 12]}>
        {WALLET_DISPLAY_ORDER.map((t) => (
          <Col xs={12} sm={8} md={6} lg={4} xl={3} key={t}>
            <Card size="small" loading={loading}>
              <Statistic
                title={walletCardTitle(t)}
                value={fmtWallet(sums?.[t])}
                valueStyle={valueColor ? { color: valueColor, fontSize: 16 } : { fontSize: 16 }}
              />
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
}

export function Dashboard() {
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api.get<Kpi>('/api/admin/dashboard'),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 30_000,
  });

  const ready = Boolean(data) && !isError;
  const cardLoading = isLoading || (isFetching && !data);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>数据概览</h2>
        <Space>
          {dataUpdatedAt > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              更新于 {new Date(dataUpdatedAt).toLocaleTimeString()}
            </Typography.Text>
          )}
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => void refetch()}>
            刷新
          </Button>
        </Space>
      </div>
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="加载统计失败"
          description={error instanceof Error ? error.message : '请点击刷新重试'}
          action={
            <Button size="small" onClick={() => void refetch()}>
              重试
            </Button>
          }
        />
      )}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="网体内账户（系谱）"
              value={statNum(data, 'genealogy_users')}
              valueStyle={ready ? { color: '#1677ff' } : undefined}
            />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              与系谱图全球玩家同口径
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="非全球玩家"
              value={
                data
                  ? (data.out_of_network_users ?? 0) + (data.php_only_out_of_network ?? 0)
                  : '—'
              }
              valueStyle={ready ? { color: '#d48806' } : undefined}
            />
            <Typography.Text style={{ fontSize: 11 }}>
              <Link to="/platform/binary_tree?tab=out">系谱图 → 非全球玩家</Link>
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="全量镜像未冻结（含非网体）"
              value={statNum(data, 'active_users')}
              valueStyle={ready ? { color: '#3f8600' } : undefined}
            />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              不含虚拟；≠ 系谱网体内
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic title="用户总数（全量镜像）" value={statNum(data, 'total_users')} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="虚拟用户"
              value={statNum(data, 'virtual_users')}
              valueStyle={ready ? { color: '#8c8c8c' } : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="冻结账户"
              value={statNum(data, 'frozen_users')}
              valueStyle={ready ? { color: '#cf1322' } : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic title="24 小时活跃" value={statNum(data, 'active_users_24h')} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="待审核充值"
              value={statNum(data, 'recharge_pending')}
              valueStyle={ready ? { color: '#faad14' } : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="待审核提现"
              value={statNum(data, 'withdraw_pending')}
              valueStyle={ready ? { color: '#faad14' } : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic title="EP 挂单中" value={statNum(data, 'ep_open_orders')} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={cardLoading}>
            <Statistic
              title="ORB(主+子) 合计"
              value={data?.total_uo_balance ? fmtWallet(data.total_uo_balance) : '—'}
            />
          </Card>
        </Col>
      </Row>

      <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
        下方钱包金额为全量主账户汇总（非系谱过滤），与前端资产概况同口径。
      </Typography.Paragraph>

      <WalletSection
        title="冻结主账户钱包合计（与前端资产概况同口径）"
        sums={data?.wallet_frozen}
        loading={cardLoading}
        valueColor="#cf1322"
      />
      <WalletSection
        title="正常主账户钱包合计（不含虚拟用户，与前端资产概况同口径）"
        sums={data?.wallet_active}
        loading={cardLoading}
        valueColor="#3f8600"
      />
      <WalletSection
        title="虚拟用户钱包合计（全部虚拟账户）"
        sums={data?.wallet_virtual}
        loading={cardLoading}
        valueColor="#8c8c8c"
      />

      <Divider orientation="left" style={{ marginTop: 8 }}>
        保留区主号 ORB（玩家 100100–100125、100129）
      </Divider>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 12, fontSize: 12 }}>
        仅显示仍在网体（全球玩家）内的保留区主号；系谱已摘除的不展示（可在非全球玩家查）。
        与前端钱包 ORB(主+子) 同算法：主号 + 子账户 floor(raw_uo)，扣 AP 捐币池。
      </Typography.Paragraph>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Card size="small" loading={cardLoading}>
            <Statistic title="保留区 ORB(主) 合计" value={fmtWallet(data?.preserved_orb_sum_uo_main)} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card size="small" loading={cardLoading}>
            <Statistic
              title="保留区 ORB(主+子) 合计"
              value={fmtWallet(data?.preserved_orb_sum_uo_total)}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card size="small" loading={cardLoading}>
            <Statistic title="主号数量" value={data?.preserved_orb_accounts?.length ?? '—'} />
          </Card>
        </Col>
      </Row>
      <Card size="small" loading={cardLoading} styles={{ body: { padding: 0 } }}>
        <Table<PreservedOrbRow>
          size="small"
          bordered
          rowKey="player_number"
          pagination={false}
          scroll={{ x: 640 }}
          dataSource={data?.preserved_orb_accounts ?? []}
          columns={[
            { title: '玩家编号', dataIndex: 'player_number', width: 100, fixed: 'left' },
            { title: '账号', dataIndex: 'account', width: 160, ellipsis: true },
            {
              title: '状态',
              dataIndex: 'freeze',
              width: 80,
              align: 'center',
              render: (v: PreservedOrbRow['freeze']) => (
                <Tag color={v === 'frozen' ? 'red' : 'green'}>{v === 'frozen' ? '冻结' : '正常'}</Tag>
              ),
            },
            {
              title: 'ORB(主)',
              dataIndex: 'uo_main',
              width: 110,
              align: 'right',
              render: (v: string) => Number(v).toLocaleString(),
              sorter: (a, b) => Number(a.uo_main) - Number(b.uo_main),
            },
            {
              title: 'ORB(主+子)',
              dataIndex: 'uo_total',
              width: 120,
              align: 'right',
              render: (v: string) => Number(v).toLocaleString(),
              defaultSortOrder: 'descend',
              sorter: (a, b) => Number(a.uo_total) - Number(b.uo_total),
            },
            {
              title: '子账户',
              dataIndex: 'sub_count',
              width: 80,
              align: 'right',
              sorter: (a, b) => a.sub_count - b.sub_count,
            },
          ]}
        />
      </Card>
    </div>
  );
}
